"use client";

import { motion, AnimatePresence } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { INTRO_DURATION_MS } from "@/lib/intro-gate";

const words = ["Deci", "cauți", "un", "psihoterapeut?"];
const dots = ["#2C969B", "#DC7050", "#FECA2E"];

type ExitTarget = { dx: number; dy: number; scale: number };

export function MindtaleIntro({ onFinish }: { onFinish?: () => void }) {
  const [done, setDone] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [exitTarget, setExitTarget] = useState<ExitTarget | null>(null);
  const textRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => setMounted(true), []);

  // The portal is in the DOM once `mounted` flips, on top of the server
  // rendered curtain (same background), so the hand-off is invisible.
  useEffect(() => {
    if (mounted) document.getElementById("mindtale-curtain")?.remove();
  }, [mounted]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      // FLIP: measure where the hero headline sits so the question can
      // fly into its place. Falls back to a simple upward exit when the
      // page has no hero headline.
      const hero = document.querySelector("[data-hero-headline]");
      const intro = textRef.current;
      if (hero && intro) {
        const h = hero.getBoundingClientRect();
        const i = intro.getBoundingClientRect();
        const heroFont = parseFloat(getComputedStyle(hero as Element).fontSize);
        const introFont = parseFloat(getComputedStyle(intro).fontSize);
        setExitTarget({
          dx: h.left - i.left,
          dy: h.top - i.top,
          scale: heroFont / introFont,
        });
      }
      setDone(true);
      onFinish?.();
    }, INTRO_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [onFinish]);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {!done && (
        <div key="intro" className="fixed inset-0 z-[9999]">
          {/* Curtain panel */}
          <motion.div
            initial={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.75, ease: [0.76, 0, 0.24, 1] }}
            className="absolute inset-0 bg-[#FDF9F5] shadow-[0_30px_60px_rgba(38,70,83,0.18)]"
          />

          {/* The question — flies into the hero headline's seat */}
          <div className="pointer-events-none absolute inset-x-0 top-0 flex h-[100svh] items-center justify-center px-6">
            <motion.h1
              ref={textRef}
              exit={
                exitTarget
                  ? {
                      x: exitTarget.dx,
                      y: exitTarget.dy,
                      scale: exitTarget.scale,
                      opacity: 0,
                    }
                  : { y: -120, opacity: 0 }
              }
              transition={
                exitTarget
                  ? {
                      duration: 0.75,
                      ease: [0.76, 0, 0.24, 1],
                      opacity: { delay: 0.4, duration: 0.35 },
                    }
                  : { duration: 0.5, ease: [0.76, 0, 0.24, 1] }
              }
              className="origin-top-left text-center font-sans text-[clamp(1.75rem,6vw,3.5rem)] font-extrabold leading-tight tracking-[-0.02em] text-[#264653]"
            >
              {words.map((word, i) => (
                <motion.span
                  key={word}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.45,
                    delay: 0.15 + i * 0.14,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className="inline-block whitespace-pre"
                >
                  {word}
                  {i < words.length - 1 ? " " : ""}
                </motion.span>
              ))}
              <span className="ml-2 inline-flex items-baseline gap-1.5" aria-hidden>
                {dots.map((color, i) => (
                  <motion.span
                    key={color}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: [0, 1.3, 1] }}
                    transition={{
                      duration: 0.4,
                      delay: 0.95 + i * 0.14,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="size-[0.14em] rounded-full"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </span>
            </motion.h1>
          </div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
